import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { Title, ControlledTabs, useTabState, Box } from '@nerdwallet/currency';

import analytics from '@nerdwallet/analytics';
import {
  trackImpression,
  trackInteraction,
} from '../../../lib/analytics-helpers';
import VerticalComparisonTab from './vertical-comparison-tab';

import styles from './vertical-comparison.module.less';

const IconAndTitle = ({ title, icon }) => {
  const Icon = icon;
  return (
    <div>
      <Box paddingBottom={2}>
        <Icon size={35} />
      </Box>
      {title}
    </div>
  );
};

const VerticalComparison = ({ title, tabs, backgroundImageUrl }) => {
  const mappedTabs = tabs.map((tab) => ({
    label: <IconAndTitle title={tab.title} icon={tab.icon} />,
    id: tab.id,
    content: <VerticalComparisonTab {...tab} />,
  }));

  const tabState = useTabState({
    tabs: mappedTabs,
    baseId: 'verticalComparisonTab',
  });

  const tabBackgroundImage = {
    backgroundImage: `url(${backgroundImageUrl})`,
  };

  const tabImpressionId = useRef(analytics.uuid());
  const previousSelectedIndexForTracking = useRef(tabState.selectedIndex);

  useEffect(() => {
    const selectedLabel = tabs?.[tabState.selectedIndex]?.id;

    if (selectedLabel) {
      const interactionEntityName = `hp_${selectedLabel}_tab_clicked`;
      const impressionEntityName = `hp_${selectedLabel}_tab_successful_display`;

      // Storing the previous index in a ref allows us to avoid firing an event
      // on the initial render, as the "previous" index will match the "current"
      // index because of how this ref is initialized.
      if (previousSelectedIndexForTracking.current !== tabState.selectedIndex) {
        trackInteraction(interactionEntityName, tabImpressionId.current);
        previousSelectedIndexForTracking.current = tabState.selectedIndex;
      }

      trackImpression(impressionEntityName);
    }
  }, [tabs, tabState.selectedIndex]);

  return (
    <div className={styles.wrapper}>
      <Title className={styles.titleWrap}>{title}</Title>
      <div className={styles.tabsWrapper} style={tabBackgroundImage}>
        <ControlledTabs
          hasSpacingBetweenTabsAndContent={false}
          hasBorderBetweenTabsAndContent={false}
          tabState={tabState}
        />
      </div>
    </div>
  );
};

IconAndTitle.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.elementType,
};

VerticalComparison.propTypes = {
  title: PropTypes.string,
  backgroundImageUrl: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape(VerticalComparison.propTypes)),
};

VerticalComparison.defaultProps = {};

export default VerticalComparison;
